import { apiFetch } from './client';

// All address autocomplete + place lookup goes through the API server, never
// directly to Google. Keeps the API key out of the bundle, lets CSP stay
// tight, and centralizes provider quirks server-side. See internal/geocode/.

export interface GeocodePrediction {
  place_id: string;
  main_text: string;
  secondary_text: string;
  formatted_text: string;
}

export interface GeocodePlace {
  place_id: string;
  formatted_address: string;
  street: string;
  postal_code: string;
  city: string;
  country_code: string;
  latitude: number;
  longitude: number;
}

interface AutocompleteResponse {
  predictions: GeocodePrediction[];
}

// newSessionToken returns a UUID for the current typing session. Google
// bills Autocomplete requests as "free" when paired with a Place Details
// call sharing the same session token, so the SPA generates one at the
// start of typing and keeps it until the user picks a result (or cancels).
export function newGeocodeSessionToken(): string {
  // crypto.randomUUID is available in all our supported browsers and in
  // any secure context (which includes http://localhost during dev).
  return crypto.randomUUID();
}

// kinds picks the result-type filter on the backend. Default ("" or
// "address") restricts to street / route / premise — right for an
// office-address field. "any" lifts the filter so establishments
// (sports halls, stadiums, parks) come back too — right for a venue picker.
export type GeocodeKinds = '' | 'address' | 'any';

export async function geocodeAutocomplete(
  token: string | null,
  query: string,
  sessionToken: string,
  language: string,
  region: string,
  kinds: GeocodeKinds = '',
): Promise<GeocodePrediction[]> {
  const params = new URLSearchParams({ q: query });
  if (sessionToken) params.set('session_token', sessionToken);
  if (language) params.set('language', language);
  if (region) params.set('region', region);
  if (kinds) params.set('kinds', kinds);
  const res = await apiFetch<AutocompleteResponse>(
    `/api/v1/geocode/autocomplete?${params.toString()}`,
    { token },
  );
  return res.predictions;
}

export async function geocodePlaceDetails(
  token: string | null,
  placeId: string,
  sessionToken: string,
  language: string,
): Promise<GeocodePlace> {
  const params = new URLSearchParams({ place_id: placeId });
  if (sessionToken) params.set('session_token', sessionToken);
  if (language) params.set('language', language);
  return apiFetch<GeocodePlace>(`/api/v1/geocode/details?${params.toString()}`, { token });
}

// geocodeReverse turns a browser geolocation reading into a structured
// Place. The server hits Google's Geocoding API; the SPA never sees the
// API key. Result quality depends entirely on the device — phone GPS gives
// a usable street address, desktop WiFi triangulation often only reaches
// city/country accuracy.
export async function geocodeReverse(
  token: string | null,
  lat: number,
  lng: number,
  language: string,
): Promise<GeocodePlace> {
  const params = new URLSearchParams({
    lat: lat.toString(),
    lng: lng.toString(),
  });
  if (language) params.set('language', language);
  return apiFetch<GeocodePlace>(`/api/v1/geocode/reverse?${params.toString()}`, { token });
}
